import React, { useState, Dispatch, SetStateAction, useEffect, useCallback } from 'react';
import { Dialog, Radio, Transfer } from '@alifd/next';
import Button from '@/components/Button';
import { Message, Loading } from '@b-design/fusion';
import store from '@/store';
import $i18n from '@/i18n';
import './index.less';
import Requests from '@/services/request';

const RadioGroup = Radio.Group;

const RadioOptions = [
  {
    value: 'ADMIN',
    title: $i18n.get({
      id: 'aopf.pages.member.actionSetAsAdmin',
      dm: '管理员',
    }),
    desc: $i18n.get({
      id: 'aopf.pages.member.actionSetAsAdminDesc',
      dm: '管理所有应用程序的最高权限',
    }),
  },
  {
    value: 'DEVELOPER',
    title: $i18n.get({
      id: 'aopf.pages.member.actionSetAsDeveloper',
      dm: '开发者',
    }),
    desc: $i18n.get({
      id: 'aopf.pages.member.actionSetAsDeveloperDesc',
      dm: '开发与测试应用',
    }),
  },
];

interface AddMemberModal {
  addMemberVisible: boolean;
  setAddMemberVisible: Dispatch<SetStateAction<boolean>>;
  spaceId: string | (string | null)[] | null;
  appId: string | (string | null)[] | null;
  queryChannel: () => void;
}

const AddMemberModal: React.FC<AddMemberModal> = ({
  addMemberVisible,
  setAddMemberVisible,
  spaceId,
  appId,
  queryChannel,
}) => {
  // 所有成员
  const [allMember, setAllMember] = useState();

  // 新增成员loading
  const [loading, setLoading] = useState(false);

  // 添加成员角色
  const [addMemberRole, setAddMemberRole] = useState<string | undefined>('DEVELOPER');

  // 新增的成员
  const [addMemberValue, setAddMemberValue] = useState<string[]>([]);

  // 当前用户信息
  const [userState] = store.useModel('user');

  const onAddMemberClose = () => {
    // 清空数据
    setAddMemberRole('DEVELOPER');
    setAddMemberValue([]);
    setAddMemberVisible(false);
  };

  useEffect(() => {
    if (!spaceId) {
      return;
    };
    Requests.get('/rest/intercnn/space/auth/query', {
      authId: spaceId,
      currentPage: 1,
      pageSize: 1000,
    }).then((data) => {
      if (data) {
        const newList = data?.list.map((item: any) => {
          return {
            ...item,
            roleList: item?.role?.split(',') || [],
          }
        })
        // 穿梭框过滤当前用户
        const dataTemp = newList?.filter((i) => i.accountId !== userState.currentUser.accountId);
        const member = dataTemp?.map((m) => ({
          label: `${m?.userNick}(${m?.authId})`,
          value: m?.accountId,
          disabled: false,
        }));
        setAllMember(member);
      }
    });
  }, [spaceId]);

  const onOk = useCallback(async () => {
    if (!addMemberRole) {
      Message.error({
        title: $i18n.get({
          id: 'aopf.pages.member.addMemberRoleErrorTip',
          dm: '请选择角色!',
        }),
      });
    } else if (!addMemberValue?.length) {
      Message.error({
        title: $i18n.get({
          id: 'aopf.pages.member.addMemberErrorTip',
          dm: '请选择成员!',
        }),
      });
    } else if (addMemberRole && addMemberValue?.length) {
     const data = await Requests.post('/rest/intercnn/app/auth/add', {
        role: addMemberRole,
        accountIdList: addMemberValue,
        authId: appId,
      });
      if (data) {
        Message.success({
          title: $i18n.get({
            id: 'aopf.pages.miniapp.versions.form.submit.msg',
            dm: '提交成功!',
          }),
        });
        onAddMemberClose();
        queryChannel();
      }
    }
  }, [addMemberRole, addMemberValue, onAddMemberClose, appId]);

  const queryMember = useCallback(() => {
    setLoading(true);
    Requests.get('/rest/intercnn/app/auth/query', {
      currentPage: 1,
      pagesize: 10,
      authId: appId,
      role: addMemberRole,
    }).then((data) => {
      if (data) {
        const member = data?.list
          ?.map((m) => m?.accountId)
          .filter((i) => i !== userState.currentUser.accountId);
        setAddMemberValue(member);
        setLoading(false);
      }
    });
  }, [addMemberRole]);

  useEffect(() => {
    if (addMemberVisible) {
      queryMember();
    }
  }, [queryMember, addMemberVisible]);

  return (
    <div>
       <Dialog
        v2
        title={$i18n.get({ id: 'aopf.pages.member.addMembers', dm: '添加成员' })}
        visible={addMemberVisible}
        onClose={onAddMemberClose}
        footerActions={['cancel', 'ok']}
        width={680}
        className="addMember-modal"
        footer={[
          <div className="dialog-footer">
            <Button className="dialog-btn" type="normal" onClick={onAddMemberClose}>
              {$i18n.get({ id: 'aopf.component.invite.Cancel', dm: '取消' })}
            </Button>
            <Button type="primary" onClick={onOk}>
              {$i18n.get({ id: 'aopf.component.invite.Ok', dm: '确定' })}
            </Button>
          </div>,
        ]}
      >
        <RadioGroup
          className="addMemberRadio"
          value={addMemberRole}
          onChange={(value) => {
            setAddMemberRole(value as string | undefined);
            setAddMemberValue([]);
          }}
          aria-labelledby="groupId"
        >
          {[...RadioOptions]?.reverse()?.map((item) => (
            <Radio id={item?.value} value={item?.value} className="radioItem" key={item?.value}>
              <p className="roleTitle">{item.title}</p>
              <p className="roleDesc">{item.desc}</p>
            </Radio>
          ))}
        </RadioGroup>
        <Transfer
          showSearch={[true, true]}
          searchPlaceholder={$i18n.get({ id: 'aopf.pages.miniapp.input.Search', dm: '搜索...' })}
          searchProps={{
            hasClear: true,
            style: { width: '250px' },
          }}
          listStyle={{ width: '250px', height: '276px' }}
          value={addMemberValue}
          dataSource={allMember}
          onChange={setAddMemberValue}
          titles={[
            $i18n.get({ id: 'aopf.pages.member.addMemberAll', dm: '所有成员' }),
            $i18n.get({ id: 'aopf.pages.member.addMemberMiniApp', dm: '小程序成员' }),
          ]}
          notFoundContent={
            loading ? (
              <Loading visible={loading} style={{ width: '100%' }} />
            ) : (
              [
                $i18n.get({ id: 'aopf.pages.member.addMemberAllNotFound', dm: '暂无成员' }),
                $i18n.get({ id: 'aopf.pages.member.addMemberAllNotFound', dm: '暂无成员' }),
              ]
            )
          }
        />
      </Dialog>
    </div>
  );
};
export default AddMemberModal;
